import { useEffect } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { useRoomStore } from '@/stores/roomStore';
import { useUIStore } from '@/stores/uiStore';
import { useMatchStore } from '@/stores/matchStore';
import { getSocket } from '@/net/socket';
import { roomApi } from '@/rooms/roomApi';
import type { RoomDto, RoomMatchStartPayload } from '@shared/protocol';

/**
 * Always-on listener that keeps roomStore in sync regardless of which screen
 * is currently mounted. Crucial for the "BACK TO ROOM" flow: while the user
 * is on Match / Result, the server emits `room:update` to reset status →
 * 'lobby' after the match. Without this hook those events are dropped (the
 * RoomScreen listener is gone), leaving the store with stale `status: 'active'`
 * and the next START call rejected with "Already started".
 */
export function RoomNotifier() {
  const me = useAuthStore((s) => s.user);
  const setRoom = useRoomStore((s) => s.setRoom);
  const clearRoom = useRoomStore((s) => s.clear);
  const goScreen = useUIStore((s) => s.go);

  useEffect(() => {
    if (!me) return;
    const socket = getSocket();
    const onUpdate = (dto: RoomDto) => {
      setRoom(dto);
      // Search abandoned server-side (a teammate cancelled or dropped) → the
      // room flips back to 'lobby'. If we're still parked on the matchmaking
      // screen waiting for a match that's no longer coming, bounce back to the
      // lobby with our teammates instead of searching into the void.
      if (dto.status === 'lobby' && useUIStore.getState().screen === 'matching') {
        useMatchStore.getState().reset();
        goScreen('room');
      }
    };
    const onKicked = () => {
      clearRoom();
      // If the user happens to be on RoomScreen, kick them out.
      const screen = useUIStore.getState().screen;
      if (screen === 'room') goScreen('home');
    };
    const onMatchStart = (payload: RoomMatchStartPayload) => {
      setRoom(payload.room);
      // Tag the upcoming match so ResultScreen shows "BACK TO ROOM" only when
      // it really is a room-spawned match (not a stale `roomStore` from a
      // prior session bleeding into a quick match's result screen).
      useMatchStore.getState().setMatchSource(payload.mode === 'quick' ? 'queue' : 'room');
      if (payload.mode === 'team-duel' || payload.mode === 'duo-rank') {
        // 2v2: arm the team-pending flag so MatchmakingScreen awaits
        // TEAM_MATCH_FOUND instead of joining the solo queue.
        if (payload.teamMode) useMatchStore.getState().beginTeamPending(payload.teamMode);
        goScreen('matching');
      } else if (payload.mode === 'solo-duel' || payload.mode === 'quick') {
        goScreen('matching');
      }
    };
    // Reconnect re-sync. When the app is backgrounded (mobile app-switch), the
    // socket drops; on resume it reconnects and fires 'connect'. The room state
    // may have changed while away (teammate left, match started, or the server
    // evicted us past the grace window), so re-fetch the authoritative room. If
    // we're no longer in a room, clear the stale store and bounce off
    // RoomScreen — otherwise the player sits frozen on a phantom lobby.
    const onConnect = () => {
      if (!useRoomStore.getState().room) return;
      roomApi
        .mine()
        .then((r) => {
          if (r.room) {
            setRoom(r.room);
          } else {
            clearRoom();
            if (useUIStore.getState().screen === 'room') goScreen('home');
          }
        })
        .catch(() => { /* transient; next event/connect will retry */ });
    };
    socket.on('connect', onConnect);
    socket.on('room:update', onUpdate);
    socket.on('room:kicked', onKicked);
    socket.on('room:matchStart', onMatchStart);
    return () => {
      socket.off('connect', onConnect);
      socket.off('room:update', onUpdate);
      socket.off('room:kicked', onKicked);
      socket.off('room:matchStart', onMatchStart);
    };
  }, [me, setRoom, clearRoom, goScreen]);

  return null;
}
